import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_URLS } from '../../config';
import { apiRequest } from '../../utils/apiRequest';
import { FiRefreshCw, FiFileText, FiCreditCard, FiBell } from 'react-icons/fi';
import './Finance.css';

export default function FinanceDashboard() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (fromDate) params.set('fromDate', fromDate);
    if (toDate) params.set('toDate', toDate);
    const result = await apiRequest(`${API_URLS.FINANCE_DASHBOARD}?${params}`, {}, token);
    if (result?.ok) setData(result.data);
    setLoading(false);
  }, [token, fromDate, toDate]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const fmt = n => n != null ? (+n).toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '—';
  const statusLabel = (s) => s === 'INVOICE_ISSUED' ? 'Invoice Issued' : 'Proforma Invoice';

  if (loading) return <div className="fin-page"><div className="fin-loading">Loading dashboard…</div></div>;

  const { stats = {}, recentInvoices = [] } = data || {};

  return (
    <div className="fin-page">
      <div className="fin-header" style={{ marginBottom: 20 }}>
        <div>
          <h2 className="fin-title">Finance Dashboard</h2>
          <p className="fin-subtitle">Invoice requests + data overview</p>
        </div>
        <div className="fin-controls">
          <input className="fin-input" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} title="From" />
          <input className="fin-input" type="date" value={toDate} onChange={e => setToDate(e.target.value)} title="To" />
          <button className="fin-btn fin-btn-outline" onClick={fetchDashboard}><FiRefreshCw size={14} /></button>
        </div>
      </div>

      {/* Stats */}
      <div className="fin-stats-grid">
        <StatCard icon={<FiFileText size={20} color="#1a3a6b" />} label="Total Invoices" value={stats.totalInvoices ?? 0} sub={`${stats.confirmedInvoices ?? 0} issued`} />
        <StatCard icon={<FiCreditCard size={20} color="#16a34a" />} label="Total Receivables" value={`₹${fmt(stats.totalReceivables)}`} sub="Issued, not yet sent" />
        <StatCard icon={<FiBell size={20} color="#6366f1" />} label="Unread Notifications" value={stats.unreadNotifications ?? 0} sub="Pending actions" />
      </div>

      {/* Recent activity */}
      <div className="fin-card">
        <h3 className="fin-section-title" style={{ marginTop: 0 }}>Recent Invoices</h3>
        <div className="fin-table-wrap" style={{ border: 0 }}>
          <table className="vendor-table">
            <thead><tr><th>Invoice #</th><th>Job #</th><th>Customer</th><th>Total</th><th>Status</th></tr></thead>
            <tbody>
              {recentInvoices.length === 0 ? (
                <tr><td colSpan={5} className="fin-empty">No invoices</td></tr>
              ) : recentInvoices.map(r => (
                <tr key={r._id}>
                  <td>{r.invoiceNumber}</td>
                  <td>{r.jobNumber || '—'}</td>
                  <td>{r.customerName || '—'}</td>
                  <td>{fmt(r.total)} {r.currency}</td>
                  <td><span className={`fin-badge ${r.status === 'INVOICE_ISSUED' ? 'fin-badge-confirmed' : 'fin-badge-draft'}`}>{statusLabel(r.status)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, sub }) {
  return (
    <div className="fin-stat-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        {icon}
        <span className="fin-stat-label" style={{ margin: 0 }}>{label}</span>
      </div>
      <div className="fin-stat-value">{value}</div>
      {sub && <div className="fin-stat-sub">{sub}</div>}
    </div>
  );
}
